import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useConfirm } from '@/components/feedback/dialog';
import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { SelectField, TextField } from '@/components/forms';
import { ApiError, type Envelope } from '@/lib/api/errors';
import { parseWarrantyUnitValue, unitLabel, warrantyUnitValue } from '@/lib/orders';

import { toDeviceOptions } from '../format';
import {
  isNoLongerConfigurable,
  isSerialNotFound,
  useApplyParamTemplate,
  useConfirmParamTemplate,
  useEligibleDevices,
  useParamTemplates,
  useProductTypes,
  useVerifySerial,
} from '../hooks';
import { addParametersSchema } from '../schemas';
import type { AddParametersValues, OrderDevice, OrderScope } from '../types';

import styles from './add-parameters-modal.module.scss';

/** Figma 401:273008 / AC2, verbatim. */
const TITLE = 'Add Parameters';
const INSTRUCTION = 'Enter the serial number found on the back of your device.';
/** Figma `12:234312` / AC2, verbatim — the body keeps its trailing period, being a full sentence. */
const DISCARD_TITLE = 'Discard changes?';
const DISCARD_BODY = 'Any unsaved parameter settings and device selections will be lost.';
/** Figma `401:273824`, verbatim — **no trailing period**, house style for single-sentence inline
 *  errors (`'File size must not exceed 5MB'`); periods are for multi-sentence ones. */
const INVALID_SERIAL = 'Invalid serial number';
/** Undrawn. A transport failure is neither a verdict nor an advance, so it is a toast rather than an
 *  inline error — the shipped intake-drawer pattern, which prefers the server's own message. */
const VERIFY_FAILED = 'Could not verify the serial number. Please try again.';
/** AC5, verbatim — kept over Figma's generic "No Templates Found" because Digital Matter is the only
 *  place this dead end can be fixed (grilling Q10, FLAG-BA-3). No trailing period: house style puts one
 *  only on multi-sentence inline copy. */
const NO_TEMPLATES = 'No templates available. Create one in Digital Matter';

/** AC2, verbatim — the acceptance criteria's plural. */
const APPLIED = 'Parameter templates added successfully';
/** AC4, verbatim. Two sentences, so it keeps its period (house style). */
const APPLY_FAILED = 'Failed to save parameter configuration. Please try again.';
/** AC5, verbatim. Shown only when the server supplies no message of its own (AC6). */
const ALREADY_CONFIGURED = 'This device has already been configured.';

/** Undrawn, and the one message this ticket authors. The set succeeded — the template **is** on the
 *  device — so it leads with that and never with a failure; only the confirmation is missing. It also
 *  stops short of asking for anything: AC6 forbids inviting a retry of the apply, and these two facts
 *  are the whole of what the AC asks be said. Both keep their periods, being full sentences, as
 *  `ALREADY_CONFIGURED` does. */
const AWAITING_CONFIRMATION = 'Parameter template applied.';
const AWAITING_CONFIRMATION_BODY = 'The device is awaiting confirmation.';

/** Figma `216:261822`, verbatim — Step 3's own title, replacing the wizard's. */
const PREVIEW_TITLE = 'Preview';
/**
 * Undrawn, and authored here (AC3). Step 3 shows **no parameter values**: the apply hands back the live
 * ones, but as numeric group ids and raw codes with no labels, tab names or enum option names — those
 * live only in the Digital Matter parameter dictionary, which no endpoint serves and which the frontend
 * must never parse (ADR-0024). A screen of `GPS_PDOP` / `2100` rows reads as a bug rather than as
 * information, so the admin gets the two facts that are true and legible instead.
 *
 * Deliberately unlike both toasts this flow emits — `APPLIED` on success and `AWAITING_CONFIRMATION` on
 * a failed confirm — because a near-identical string describing a *different* state is a copy hazard.
 * `Pending QC` is the Device Status label the portal itself renders. The heading takes no period and the
 * full-sentence detail keeps one (house style).
 */
const APPLIED_HEADING = 'Configuration complete';
const APPLIED_DETAIL =
  'The parameter template was applied. Select Complete to move the device to Pending QC.';
/**
 * Figma `216:261822`'s summary line. Drawn as five text nodes with an 8px gap; one string carries the
 * same reading and needs no markup of its own — the shared chrome's `subtitle` is already Geist 14 /
 * `--text-muted`, exactly what the frame specifies.
 */
const summaryLine = (device: string, serial: string, template: string) =>
  `Device: ${device} • SN No: ${serial} • Template: ${template}`;
/**
 * Figma draws the Preview at 906 against steps 1–2's 709 (AC12). `<Modal size="lg">` is 709 and
 * `width` wins over `size` when set, so passing `undefined` on steps 1–2 falls back to it.
 */
const PREVIEW_WIDTH = 906;

/**
 * The message the **server** sent, if it sent one. `ApiError.message` cannot be used directly here:
 * when the envelope carries no message, `parseApiError` falls back to axios's "Request failed with
 * status code 409", which is not admin copy. ADR-0008 prefers the server's own message, so read the
 * envelope and let the AC's locked string fill the gap (AC6). `Envelope` is the same shape
 * `parseApiError` reads internally, exported from `errors.ts` rather than re-declared here.
 */
const serverMessage = (error: unknown): string | undefined =>
  error instanceof ApiError
    ? (error.data as Envelope | null | undefined)?.error?.message
    : undefined;

/**
 * Step 1's fields, **picked** from the whole-form schema rather than declared a second time. `pick`
 * keeps working now that ticket 05 has added step 2's fields, so the live-enable gate below cannot
 * silently start demanding them and leave Next permanently off.
 */
const step1Slice = addParametersSchema.pick({ device: true, serialNumber: true });

/** Step 2's fields, `pick`ed from the same schema for the same reason — so neither step's gate can
 *  silently start demanding the other's fields. */
const step2Slice = addParametersSchema.pick({ productName: true, templateId: true });

/** AC8, verbatim — two sentences, so it keeps its period (house style: no period on single-sentence
 *  inline errors like 'Invalid serial number', one on multi-sentence copy). */
const CATEGORY_MISMATCH =
  "This template does not match the selected device's category. Please choose a compatible template.";

/** Digits only. The seven-digit cap is native `maxLength` (which also truncates a paste), so this
 *  strips and nothing else — "rejected as they are typed", not validated afterwards (AC1). */
const digitsOnly = (raw: string) => raw.replace(/\D/g, '');

/** The wizard's three steps. A union rather than `number` is what makes the primary map below
 *  exhaustive at compile time — a step ticket 10 adds cannot silently fall through it. */
type Step = 1 | 2 | 3;

export type AddParametersModalProps = {
  /** Where the device list comes from. Parameterised rather than forked per host (ADR-0024); ticket
   *  09 widens this to include the customer scope. */
  scope: OrderScope;
  open: boolean;
  onClose: () => void;
};

/**
 * Applies a Digital Matter **Parameter Template** to one **Unit**. Step 1 — choose the device, confirm
 * its serial against Digital Matter — Step 2 — choose the DM Category and Parameter Template, then
 * **Set Parameter** applies it — and **Step 3** is the read-only Preview of what was written, whose
 * **Back** returns to Step 2 so the template can be changed and re-applied.
 *
 * **Step 3 shows no parameter values, and makes no request.** It was built against
 * `GET param-templates/preview`, a route the backend never deployed, so in the real app it could only
 * ever render its error state (ticket 14). The apply does return the live Digital Matter values, but
 * unlabelled — group ids and raw codes, with the human vocabulary only in the parameter dictionary — so
 * Step 3 states plainly that the template was applied instead of drawing a grid of codes. **This is
 * interim and reversible:** if the backend ever serves the joined payload, the designed tabbed view
 * returns here and nothing else about the flow changes.
 *
 * A plain `<Modal>`, not `<FormModal>`: the primary button's label *and action* change per step
 * (**Next** advances, **Set Parameter** applies, **Complete** dismisses), which `<FormModal>`'s
 * one-form-one-submit wiring fights (ADR-0024). One `useForm` spans all three steps, so RHF's
 * `shouldUnregister: false` default makes "Previous preserves my data" free rather than coded.
 *
 * `maskClosable={false}` on every step, not just Preview: the BA requires it for Preview
 * (comment 20812344 point 6) and the discard ACs only ever name the Close (X) icon, so firing
 * anything from a mask click was our own addition and is withdrawn. Ticket 07's discard guard relies on
 * this too — the X is the only way out.
 */
export function AddParametersModal({ scope, open, onClose }: AddParametersModalProps) {
  const [step, setStep] = useState<Step>(1);
  const toast = useToast();
  const form = useForm<AddParametersValues>({
    resolver: zodResolver(addParametersSchema),
    // Every field is seeded, including step 2's. RHF types `defaultValues` as a `DeepPartial`, so an
    // omission is not a type error — it would simply make `watch` yield `undefined` and the
    // comparisons below reason about a value that never came from the admin.
    defaultValues: {
      device: scope.preselect ?? '',
      serialNumber: '',
      productName: '',
      templateId: '',
    },
    mode: 'onChange',
  });
  const { data: devices, isPending } = useEligibleDevices(scope);
  const verify = useVerifySerial();
  const { confirm } = useConfirm();
  // Read during render so RHF's formState proxy subscribes to it (the shipped `customer-form`
  // idiom); read inside the handler instead and the guard stays frozen on its mount-time value.
  const { isDirty } = form.formState;
  const apply = useApplyParamTemplate(scope);
  const confirmApply = useConfirmParamTemplate(scope);
  /** The row `set` created or overwrote — held until Complete confirms it. A Back → re-apply overwrites
   *  the same row, so the latest `set` always holds the id Complete must send. */
  const [customerDeviceId, setCustomerDeviceId] = useState<string | null>(null);

  const [device, serialNumber, productName, templateId] = form.watch([
    'device',
    'serialNumber',
    'productName',
    'templateId',
  ]);

  // The row currently chosen in the dropdown, resolved from the **live** eligible list. Only Step 1
  // uses it, and only to verify: a device must still be eligible to be verified at all.
  const selectedDevice = devices?.find(
    (d) => warrantyUnitValue(d.orderItemId, d.unitIndex, d.bundleDeviceId ?? null) === device,
  );

  /**
   * The device the flow is **acting on**, captured the moment Digital Matter confirms its serial.
   *
   * Deliberately *not* re-derived from `devices` past Step 1. A successful apply invalidates the
   * eligible-units query, and the backend is the eligibility oracle that **excludes already-configured
   * Units** (ADR-0024) — so the refetch legitimately returns a list without this device. Every later
   * consumer would then read `undefined`: the Preview summary would render "Device:  • SN No: …" with a
   * hole in it (AC1), `handleApply`'s guard would make Back → re-apply a silent no-op (AC9), and the
   * category-mismatch gate would quietly switch itself off. The dropdown still lists exactly what the
   * oracle returns — this holds only the one row the wizard has already committed to.
   */
  const [actingDevice, setActingDevice] = useState<OrderDevice | null>(null);

  const categories = useProductTypes();
  const templates = useParamTemplates(productName);

  // The applied template's display name, for the Preview summary (AC1). The preview payload carries
  // only `{tabs}`, so the name comes from the list Step 2 already holds in cache under the same key.
  const templateName = templates.data?.find((t) => String(t.templateId) === templateId)?.name ?? '';

  /**
   * AC4's other half. Templates are fetched **per category**, so a template chosen under the previous
   * category is not merely stale — antd renders a value absent from `options` as its raw string, and it
   * would keep `step2Complete` true with a template that cannot apply to the device. Clearing is the
   * correctness fix, not a tidy-up.
   *
   * Not a composed `onChange` on the select: `<SelectField>` spreads `{...rest}` *before*
   * `onChange={field.onChange}`, so an `onChange` passed as a prop is silently clobbered — the same
   * shared-component shape that made ticket 04 add `transform` to `<TextField>`.
   */
  useEffect(() => {
    form.setValue('templateId', '');
  }, [productName, form]);

  // Derived from watched values against the step's zod slice — never `form.trigger()`, which reports
  // validity only at click time while the ACs want the primary live-disabled, and which this repo has
  // already seen fail to surface an error on a freshly-mounted field (ADR-0024).
  //
  // `errors.serialNumber` here can only be the server's verdict: a length failure already makes the
  // parse fail, so this second clause adds exactly one thing — "Next stays disabled until the serial
  // is changed" (AC5, AC6 of ticket 04).
  const step1Complete =
    step1Slice.safeParse({ device, serialNumber }).success && !form.formState.errors.serialNumber;

  /**
   * The device's DM Category, **when its name is one**. `order-devices` carries the catalog name, which
   * is a Digital Matter product type only if someone named the product that way — the live "sample
   * Device" is not (QA 2026-10-07). Until the backend names the real type (BE 115159), an unknown one is
   * `null`: Step 2 then opens on the placeholder and the apply-time 422 is the only mismatch verdict.
   */
  const isCategory = (name: string) => categories.data?.includes(name) ?? false;
  const deviceCategory =
    actingDevice !== null && isCategory(actingDevice.productName) ? actingDevice.productName : null;

  /**
   * AC7's second gate and AC8's message. **Derived, not `setError`** — ADR-0024 planned this check for
   * the primary's click handler beside serial verification, but AC7 live-*disables* Set Parameter on a
   * mismatch, so a click handler could never run to produce the verdict. And **no request** (AC10): DM
   * Category *is* the product type (grilling Q4), so a compatible category is by definition the device's
   * own, and the whole rule is this comparison.
   */
  const categoryMismatch =
    productName !== '' && deviceCategory !== null && productName !== deviceCategory;

  const step2Complete =
    step2Slice.safeParse({ productName, templateId }).success && !categoryMismatch;

  /**
   * Verification runs **here, in the Next handler** — not in the resolver, which would re-issue it on
   * every keystroke. The verdict lands via `setError`, which live-disables Next through the error
   * state *and* clears itself on the next keystroke, delivering "disabled until the serial is
   * changed" with no extra state (ADR-0024).
   */
  const handleNext = () => {
    // Unreachable while every value comes from the dropdown, but ticket 08's `preselect` can carry a
    // Unit the oracle no longer returns. Doing nothing is right — a device absent from the eligible
    // response must not be verified — and the admin can still pick from the list.
    if (!selectedDevice) return;
    verify.mutate(
      // String in the form, number on the wire. The serial alone: the deployed validator takes
      // nothing else, so "belongs to a different device" is refused at apply time instead (ticket 12).
      Number(serialNumber),
      {
        onSuccess: ({ valid }) => {
          if (!valid) {
            form.setError('serialNumber', { message: INVALID_SERIAL });
            return;
          }
          // AC2's preselect, written at the one moment the verified device is in hand. It deliberately
          // re-runs on a Previous → Next round: the category belongs to whichever device was just
          // confirmed, so re-confirming a device re-derives it.
          form.setValue(
            'productName',
            isCategory(selectedDevice.productName) ? selectedDevice.productName : '',
          );
          // The same moment is the only one at which this row is guaranteed to exist, so it is captured
          // rather than looked up again later — see `actingDevice` above.
          setActingDevice(selectedDevice);
          setStep(2);
        },
        onError: (e) => {
          if (isSerialNotFound(e)) {
            form.setError('serialNumber', { message: INVALID_SERIAL });
            return;
          }
          toast.error({ title: e instanceof ApiError ? e.message : VERIFY_FAILED });
        },
      },
    );
  };

  /**
   * Everything AC8 asks for: the form back to its defaults and the wizard back to Step 1. The host
   * only flips `open` — this component stays mounted between opens — so a reopen would otherwise
   * show the previous attempt's values. Bare `reset()` restores `defaultValues`, which already
   * spells out `scope.preselect ?? ''`; ticket 08 changing the preselect between opens is its own
   * reset-on-open problem, not this one's.
   */
  const closeAndReset = () => {
    form.reset();
    setStep(1);
    setCustomerDeviceId(null);
    onClose();
  };

  /**
   * **Complete confirms** — the second of the two calls, moving the device to Pending QC. It is deferred
   * to here, not chained to Set Parameter, because the backend refuses a second `set` once a device is
   * Pending QC, which is what Back → re-apply needs (`useApplyParamTemplate`, QA 2026-10-08). A failed
   * confirmation still ends the flow: the template **is** on the device, and the backend keeps the Unit
   * eligible so re-running the wizard finishes the job (AC6).
   */
  const handleComplete = () => {
    if (!customerDeviceId) {
      closeAndReset();
      return;
    }
    confirmApply.mutate(customerDeviceId, {
      onSuccess: (confirmed) => {
        if (!confirmed) {
          toast.warning({ title: AWAITING_CONFIRMATION, description: AWAITING_CONFIRMATION_BODY });
        }
        closeAndReset();
      },
    });
  };

  /**
   * Fired from the close (X) **only** — `maskClosable={false}` means a mask click never reaches
   * here, and the footer's Previous has its own `onCancel`. A pristine form closes with no
   * interruption (AC1); `isDirty` is RHF's own deep comparison against `defaultValues`, so a
   * ticket-08 preselected device is correctly *not* dirty and clearing a field back to empty
   * correctly stops being dirty.
   */
  const handleClose = async () => {
    // On Step 3 the template is already on the device, so there is nothing to discard — the X finishes
    // the job exactly as Complete does rather than stranding the device in Pending Confirmation.
    if (step === 3) {
      handleComplete();
      return;
    }
    if (isDirty) {
      const ok = await confirm({
        title: DISCARD_TITLE,
        subtitle: DISCARD_BODY,
        cancelText: 'Continue Setup',
        confirmText: 'Discard',
        danger: true,
        // No `icon`: the dialog's default illustration is already the dark-green trash badge
        // Figma 12:234320 draws (`src/assets/images/delete.webp`).
      });
      // Continue Setup — the dialog closes itself and nothing else happens (AC5).
      if (!ok) return;
    }
    closeAndReset();
  };

  /**
   * AC1. The Unit is identified by the **full triple**, split back out of the composite option value
   * with `parseWarrantyUnitValue` from **`@/lib/orders`** — never from `@/features/orders`, which
   * would close the cycle ADR-0024 hoisted the codec to `lib` to avoid.
   *
   * Success lands **here**, not at Complete: the device is configured on the server the moment this
   * resolves, so deferring the toast and the cache refresh would leave the portal calling a configured
   * device "Pending Configuration" for any admin who closes at Preview (grilling Q16).
   */
  const handleApply = () => {
    // `actingDevice`, never the live list: the first apply removes this Unit from the eligible response,
    // so a list-derived guard would turn the BA's retained Back → re-apply into a silent no-op (AC9).
    if (!actingDevice) return;
    const { orderItemId, unitIndex, bundleDeviceId } = parseWarrantyUnitValue(device);
    apply.mutate(
      {
        orderItemId,
        unitIndex,
        bundleDeviceId,
        serialNumber: Number(serialNumber),
        productName,
        templateId: Number(templateId),
      },
      {
        onSuccess: (result) => {
          setCustomerDeviceId(result.customerDeviceId);
          toast.success({ title: APPLIED });
          setStep(3);
        },
        onError: (error) => {
          if (isNoLongerConfigurable(error)) {
            // Another admin got there first, or the order or a replacement made the Unit ineligible.
            // The flow ends rather than leaving this admin on a step whose primary can only fail again,
            // and the device has already left the dropdown — the mutation refreshed the eligible list on
            // the way out (AC5).
            toast.error({ title: serverMessage(error) ?? ALREADY_CONFIGURED });
            onClose();
            return;
          }
          // Every field is left exactly as it was, so retrying is one click (AC4) — including a serial
          // the backend refused (`SERIAL_ALREADY_IN_USE`, `TEST_ASSET_SERIAL_REQUIRED`), which Previous
          // reaches to correct.
          toast.error({ title: serverMessage(error) ?? APPLY_FAILED });
        },
      },
    );
  };

  /**
   * The primary's label **and action** change per step (ADR-0024) — which is why this is a plain
   * `<Modal>` and not `<FormModal>`. A map rather than a ternary chain so each step's primary is read
   * in one place, and so the compiler proves every step has an entry.
   */
  const primary: Record<Step, { text: string; onClick: () => void; disabled: boolean }> = {
    1: { text: 'Next', onClick: handleNext, disabled: !step1Complete },
    2: { text: 'Set Parameter', onClick: handleApply, disabled: !step2Complete },
    // Complete **confirms** — the device leaves Pending Confirmation for Pending QC here, not on Step 2,
    // so that Back → re-apply can still overwrite (QA 2026-10-08; supersedes locked decision 7).
    3: { text: 'Complete', onClick: handleComplete, disabled: false },
  };

  return (
    <Modal
      open={open}
      onClose={() => void handleClose()}
      title={step === 3 ? PREVIEW_TITLE : TITLE}
      // Step 1 carries the instruction line and Step 3 the summary; Step 2 draws neither — its frames
      // carry no subtitle text node and their body panel sits 17px higher than Step 1's (AC1).
      // `OverlayChrome` drops the line when this is nullish.
      subtitle={
        step === 1
          ? INSTRUCTION
          : step === 3
            ? summaryLine(
                actingDevice
                  ? unitLabel(
                      actingDevice.productName,
                      actingDevice.quantity,
                      actingDevice.unitIndex,
                    )
                  : '',
                serialNumber,
                templateName,
              )
            : undefined
      }
      size="lg"
      // Steps 1–2 keep `size="lg"`'s 709; Step 3 is the wider frame (AC12).
      width={step === 3 ? PREVIEW_WIDTH : undefined}
      maskClosable={false}
      loading={verify.isPending || apply.isPending || confirmApply.isPending}
      // Figma draws "Back" on Preview and "Previous" on steps 1–2 (AC7); ticket 06's README already
      // named it Back.
      cancelText={step === 3 ? 'Back' : 'Previous'}
      // Step 3 → 2 is the BA's retained Back, which AC7's re-apply depends on (comment 20812344
      // point 4); from Step 2 it goes to 1, and on Step 1 the button renders disabled and never
      // fires, so one ternary covers every step.
      onCancel={() => setStep(step === 3 ? 2 : 1)}
      cancelDisabled={step === 1}
      primaryText={primary[step].text}
      onPrimary={primary[step].onClick}
      primaryDisabled={primary[step].disabled}
    >
      {/* Step 3 is a sibling of `.stepBody`, not a child: that wrapper's 320px minimum keeps the footer
          still between steps 1 and 2, while the confirmation sets its own height from the drawn frame. */}
      {step === 3 ? (
        <div className={styles.applied}>
          {/* `aria-hidden`: the two lines below carry the meaning, and an announced glyph would read
              ahead of them. `weight="fill"` is `<Stepper>`'s completed-step treatment. */}
          <CheckCircleIcon size={32} weight="fill" className={styles.appliedIcon} aria-hidden />
          {/* A `<p>`, not a heading: the dialog's only heading is the chrome's "Preview", and adding a
              second would change its heading structure for a decorative reason. */}
          <p className={styles.appliedHeading}>{APPLIED_HEADING}</p>
          <p className={styles.appliedDetail}>{APPLIED_DETAIL}</p>
        </div>
      ) : (
        <div className={styles.stepBody}>
          {step === 1 ? (
            <div className={styles.fieldRow}>
              {/* The response IS the option list. Every exclusion the ACs name — a cancelled order, an
                already-configured Unit, an approved replacement, a non-DM device — is simply absent
                from it. Do not add frontend eligibility filtering (AC8, ADR-0024). */}
              <SelectField
                name="device"
                control={form.control}
                label="Select Device"
                placeholder="Select"
                options={toDeviceOptions(devices ?? [])}
                loading={isPending}
                required
              />
              {/* `<TextField>`, not `<NumberField>`: antd `InputNumber` brings spinners and its own
                parsing and cannot strip non-digits at the point of entry (ADR-0024, Alternatives).
                No placeholder — Figma draws none, and the instruction line already says what to
                type. */}
              <TextField<AddParametersValues, 'serialNumber'>
                name="serialNumber"
                control={form.control}
                label="Serial Number"
                inputMode="numeric"
                maxLength={7}
                transform={digitsOnly}
                required
              />
            </div>
          ) : null}
          {step === 2 ? (
            <>
              <div className={styles.fieldRow}>
                {/* DM Category ≡ the Digital Matter product type — **not** the master Category list that
                Devices and Components reference (CONTEXT.md; the same label clash as Bundle Tier).
                Every category is offered, not just the device's: AC3 says so, and a mismatched choice
                is a state AC8 represents rather than one AC3 prevents. */}
                <SelectField
                  name="productName"
                  control={form.control}
                  label="DM Category"
                  placeholder="Select"
                  options={(categories.data ?? []).map((c) => ({ value: c, label: c }))}
                  // `isFetching`, not `isPending`: a query disabled by `enabled` stays `isPending`
                  // forever, which would spin a select that is simply waiting for its input.
                  loading={categories.isFetching}
                  required
                />
                {/* Figma draws this field's placeholder as "Enter" — an input placeholder inherited from
                the Multiselect component it was drawn from, and one more instance of the placeholder
                drift `context.md` #3/#4 records. "Select" matches Step 1 and every other select. */}
                <SelectField
                  name="templateId"
                  control={form.control}
                  label="Parameter Template"
                  placeholder="Select"
                  options={(templates.data ?? []).map((t) => ({
                    value: String(t.templateId),
                    label: t.name,
                  }))}
                  loading={templates.isFetching}
                  emptyText={NO_TEMPLATES}
                  required
                />
              </div>
              {/* Frame `216:266069` *is* this state and reddens neither field, so no `status="error"` is
                set and the message sits under the row rather than inside a field — it is about the
                pair, not about one control. It lands inside `.stepBody`'s 320px, so the footer does
                not move when it appears. */}
              {categoryMismatch ? (
                <p className={styles.mismatch} role="alert">
                  {CATEGORY_MISMATCH}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
